<template>
	<Alert v-model:is-open="isOpen" :type="alert.type">{{
		alert.message
	}}</Alert>
	<AlertDontHide v-model:is-open="isOpenDontHide" :type="alertDontHide.type">
		<span v-html="alertDontHide.message"></span>
	</AlertDontHide>
	<component :is="layout" v-model:layout="layout">
		<LiteFeatureLock
			v-if="liteFeature"
			:title="liteFeature.title"
			:description="liteFeature.description"
		/>
		<router-view v-else />
	</component>
	<DaModalFrame v-model:open="modalIsOpen">
		<new-release
			:release="release"
			@close="modalIsOpen = false"
		></new-release>
	</DaModalFrame>
	<DaModalFrame v-model:open="showPopupHoliday">
		<holiday-modal
			@close="closePopupHoliday"
			@submit="markReadNotification"
		></holiday-modal>
	</DaModalFrame>
	<DaModalFrame v-model:open="showExpiredContract">
		<expired-contract
			@close="showExpiredContract = false"
			:dayLeft="dayLeft"
			:centerName="centerName"
		></expired-contract>
	</DaModalFrame>
	<DaModalFrame v-model:open="slideOpen">
		<slide-summary
			:summary="summary"
			@close="slideOpen = false"
		></slide-summary>
	</DaModalFrame>
	<DaModalFrame v-model:open="openReminder">
		<RegisterLucaHub @close="openReminder = false"></RegisterLucaHub>
	</DaModalFrame>
	<DaModalFrame v-model:open="hasNewContractModal">
		<active-contract
			@close="hasNewContractModal = false"
			:id="store.state.profile.center_id"
			@success="hasNewContractModal = false"
		></active-contract>
	</DaModalFrame>
	<DaModalFrame v-model:open="needGeneratePeriodReportModal">
		<need-generate-period-report-modal
			:listStudents="listStudentsNeedGeneratePeriodReport"
			@close="needGeneratePeriodReportModal = false"
		></need-generate-period-report-modal>
	</DaModalFrame>
	<DaModalFrame :open="showTutorialGift" :disable-close="true">
		<TutorialGiftModal @start="onTutorialGiftStart" />
	</DaModalFrame>
	<DaModalFrame :open="showTutorialWelcome" :disable-close="true">
		<TutorialWelcomeModal @start="onTutorialWelcomeStart" />
	</DaModalFrame>
	<DaModalFrame :open="showTutorialStepSuccess" :disable-close="true">
		<TutorialStepSuccessModal
			:title="tutorialSuccessMeta.title"
			:message="tutorialSuccessMessage"
			:pause-label="tutorialSuccessMeta.pauseLabel"
			:confirm-label="tutorialSuccessMeta.confirmLabel"
			@pause="onTutorialPause"
			@continue="onTutorialContinue"
		/>
	</DaModalFrame>
	<TutorialSpotlight />
	<TutorialChecklist @later="onTutorialPause" />
	<TutorialFab @click="onTutorialFabClick" @close="onTutorialPause" />
	<ScreeningFeaturePopup
		v-if="showScreeningPopup"
		:loading="snoozingScreening"
		@later="snoozeScreeningPopup"
		@explore="exploreScreening"
	/>
</template>
<script setup>
import { _LAYOUT_NAME, _ROUTER_NAME } from '@/enums';
import Alert from '@/components/elements/alert/Alert.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import AlertDontHide from '@/components/elements/alert/AlertDontHide.vue';
import { $api } from '@/services';
import DaModalFrame from '@/components/common/daModalFrame/DaModalFrame.vue';
import NewRelease from '@/views/newRelease/newRelease.vue';
import SlideSummary from '@/views/slideSummary/slideSummary.vue';
import HolidayModal from '@/views/holidays/features/HolidayModal.vue';
import ExpiredContract from '@/views/packages/expiredContract.vue';
import RegisterLucaHub from '@/views/lucahub/RegisterLucaHub.vue';
import ActiveContract from '@/views/packages/activeContract.vue';
import NeedGeneratePeriodReportModal from '@/views/record/students/modal/NeedGeneratePeriodReportModal.vue';
import TutorialGiftModal from '@/components/modules/tutorial/TutorialGiftModal.vue';
import TutorialWelcomeModal from '@/components/modules/tutorial/TutorialWelcomeModal.vue';
import TutorialStepSuccessModal from '@/components/modules/tutorial/TutorialStepSuccessModal.vue';
import TutorialSpotlight from '@/components/modules/tutorial/TutorialSpotlight.vue';
import TutorialChecklist from '@/components/modules/tutorial/TutorialChecklist.vue';
import TutorialFab from '@/components/modules/tutorial/TutorialFab.vue';
import { useTutorial } from '@/features/tutorial/useTutorial';
import { _var } from '@/enums/Common';
import LiteFeatureLock from '@/components/common/LiteFeatureLock.vue';
import ScreeningFeaturePopup from '@/views/screening/features/ScreeningFeaturePopup.vue';
import {
	getLiteLockedFeature,
	isLitePackage,
} from '@/helper/packageTier';

const route = useRoute();
const router = useRouter();
const store = useStore();
const liteFeature = computed(() => {
	if (!isLitePackage(store.state.profile)) return null;
	return getLiteLockedFeature(route.path, route.query);
});
const isOpen = computed(() => store.state.alert.message !== '');
const alert = computed(() => store.state.alert);
const alertDontHide = computed(() => store.state.alertDontHide);
const isOpenDontHide = computed(() => store.state.alertDontHide.message !== '');
const centerName = computed(() => store.state.profile.current_org.name);
const orgId = computed(() => store.state.profile.current_org.org_id);
const modalIsOpen = ref(false);
const slideOpen = ref(false);
const release = ref({});
const layout = computed(() => route.meta?.layout || _LAYOUT_NAME.NONE_LAYOUT);
const showPopupHoliday = ref(false);
const showExpiredContract = ref(false);
const hasNewHoliday = computed(() => store.state.profile.has_new_holiday);
const dayLeft = computed(() => store.state.profile.days_left_in_contract);
const hasNewContract = computed(() => store.state.profile.has_new_contract);
const isActiveOrg = computed(() => store.state.profile.is_active_org);
const isAdmin = computed(() => store.getters.isAdmin);
const showSummary = computed(() => store.state.profile.in_new_year_time_2025);
const lucaHubOpen = computed(
	() => store.state.profile.is_open_popup_reminder_lucahub_registration
);
const openReminder = ref(false);
const summary = ref({});
const hasNewContractModal = ref(false);
const listStudentsNeedGeneratePeriodReport = ref([]);
const needGeneratePeriodReportModal = ref(false);
const showScreeningPopup = ref(false);
const snoozingScreening = ref(false);
const screeningPopup = ref(null);
const screeningPopupChecked = ref(false);

const {
	canAccessTutorial,
	phase,
	showStepSuccess,
	load: loadTutorial,
	acceptGift,
	startTour,
	pause: pauseTutorial,
	continueAfterSuccess,
	stepSuccessMessage,
	stepSuccessMeta,
} = useTutorial();
const showTutorialGift = computed(
	() => canAccessTutorial.value && phase.value === 'gift'
);
const showTutorialWelcome = computed(
	() => canAccessTutorial.value && phase.value === 'welcome'
);
const showTutorialStepSuccess = computed(
	() => canAccessTutorial.value && showStepSuccess.value
);
const tutorialSuccessMessage = computed(() => stepSuccessMessage.value);
const tutorialSuccessMeta = computed(() => stepSuccessMeta.value);

const onTutorialGiftStart = () => acceptGift();
const onTutorialWelcomeStart = () => startTour();
const onTutorialPause = () => pauseTutorial();
const onTutorialContinue = () => continueAfterSuccess();
const onTutorialFabClick = () => useTutorial().resume();

const isLoggedIn = () =>
	!!(localStorage.getItem(_var.TOKEN) && localStorage.getItem(_var.SUBDOMAIN));

watch(
	() => route.path,
	(path) => {
		if (!isLoggedIn()) return;
		if (path !== '/' && !path.includes('/pdf')) getNewRelease();
	},
	{ immediate: true }
);

const staffRole = computed(() => store.state.profile?.staff?.role);

watch(
	staffRole,
	() => {
		if (!isLoggedIn()) return;
		loadTutorial();
	},
	{ immediate: true }
);

onMounted(() => {
	if (!isLoggedIn()) return;

	if (hasNewHoliday.value === true && !isLitePackage(store.state.profile)) showPopupHoliday.value = true;
	setTimeout(() => {
		if (
			dayLeft.value <= 15 &&
			dayLeft.value > 0 &&
			isAdmin.value &&
			!hasNewContract.value &&
			isActiveOrg.value
		)
			checkContract();
	}, 2000);
	checkFlag();
	setTimeout(() => {
		if (hasNewContract.value && !isActiveOrg.value)
			hasNewContractModal.value = true;
	}, 2000);

	if (showSummary.value) getYearSummary();
	setTimeout(() => {
		if (lucaHubOpen.value) {
			openReminder.value = true;
			setReminderDay();
		}
	}, 1000);
	checkNeedGeneratePeriodReport();
	fetchFeaturePopups();
});

const popupList = (body) => {
	if (Array.isArray(body)) return body;
	if (Array.isArray(body?.data)) return body.data;
	if (Array.isArray(body?.feature_popups)) return body.feature_popups;
	if (body?.data && typeof body.data === 'object') return [body.data];
	if (body?.id) return [body];
	return [];
};

const popupText = (value) => {
	if (typeof value === 'string') return value;
	if (!value || typeof value !== 'object') return '';
	return [value.code, value.key, value.name, value.slug, value.type]
		.filter((item) => typeof item === 'string')
		.join(' ');
};

const isScreeningPopup = (item) => {
	const text = [
		item?.code,
		item?.key,
		item?.feature,
		item?.feature_key,
		item?.feature_code,
		item?.type,
		item?.name,
		item?.title,
		item?.slug,
		item?.kind,
		item?.popup_type,
		item?.modal,
	]
		.map(popupText)
		.join(' ')
		.toLowerCase();
	return /screen|sang[_ -]?loc|cars|dau[_ -]?vao|sàng lọc/.test(text);
};

const fetchFeaturePopups = async () => {
	if (screeningPopupChecked.value || !isLoggedIn()) return;
	if (layout.value !== _LAYOUT_NAME.DEFAULT_LAYOUT) return;
	screeningPopupChecked.value = true;
	try {
		const res = await $api.featurePopup.list();
		const popup = popupList(res?.data).find(isScreeningPopup);
		if (popup?.id) {
			screeningPopup.value = popup;
			showScreeningPopup.value = true;
		}
	} catch (e) {
		screeningPopupChecked.value = false;
	}
};

const snoozeScreeningPopup = async () => {
	const id = screeningPopup.value?.id;
	if (!id || snoozingScreening.value) return;
	snoozingScreening.value = true;
	try {
		await $api.featurePopup.snooze(id);
		showScreeningPopup.value = false;
	} catch (err) {
		const errors = err?.response?.data?.errors;
		await store.dispatch('setAlertMessage', {
			message: Array.isArray(errors)
				? errors.join('<br/>')
				: 'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	} finally {
		snoozingScreening.value = false;
	}
};

const exploreScreening = () => {
	showScreeningPopup.value = false;
	if (route.name !== _ROUTER_NAME.SCREENING) {
		router.push({ name: _ROUTER_NAME.SCREENING });
	}
};

watch(layout, () => {
	fetchFeaturePopups();
});

const checkNeedGeneratePeriodReport = async () => {
	try {
		const res = await $api.org.needGeneratePeriodReport();
		const students = res.data?.data || [];
		listStudentsNeedGeneratePeriodReport.value = students;
		store.dispatch(
			'setHasStudentsNeedGeneratePeriodReport',
			!!res.data?.exits_students
		);
		if (students.length > 0 && !res.data.snoozed_until) {
			needGeneratePeriodReportModal.value = true;
		}
	} catch (e) {
		console.log(e);
	}
};

const getCookie = (cookieName) => {
	const cookies = document.cookie.split(';');
	for (let i = 0; i < cookies.length; i++) {
		const cookie = cookies[i].trim();
		if (cookie.indexOf(cookieName + '=') === 0) {
			return cookie.substring(cookieName.length + 1, cookie.length);
		}
	}
	return null;
};

const getNewRelease = async () => {
	let hasShown = getCookie('showNewFeature');
	if (hasShown) return;
	const date = new Date();

	const res = await $api.release.get();
	if (res.data.data) {
		release.value = res.data.data;
		modalIsOpen.value = true;
		date.setTime(date.getTime() + 14 * 24 * 60 * 60 * 1000);
	} else {
		date.setTime(date.getTime() + 1 * 24 * 60 * 60 * 1000);
	}
	const expires = 'expires=' + date.toUTCString();
	document.cookie = `showNewFeature=true; ${expires}`;
};

const getYearSummary = async () => {
	let showSummary = getCookie('showSummary');
	if (showSummary) return;
	let date = '2025-01-01';
	const res = await $api.release.getYearSummary(orgId.value, date);
	if (res.data.data) {
		summary.value = res.data.data;
		setTimeout(() => {
			slideOpen.value = true;
		}, 2000);
	}
	document.cookie = 'showSummary=' + true;
};

const closePopupHoliday = async () => {
	try {
		await $api.schedule.markReadNotification();
		showPopupHoliday.value = false;
	} catch (e) {
		console.log(e);
	}
};

const markReadNotification = async () => {
	try {
		showPopupHoliday.value = false;
		await $api.schedule.markReadNotification();
		router.push('/holiday');
	} catch (e) {
		console.log(e);
	}
};

const checkContract = async () => {
	try {
		let checkContract = getCookie('checkContract');
		if (checkContract) return;
		else showExpiredContract.value = true;
	} catch (e) {
		console.log(e);
	}
};

const checkFlag = async () => {
	try {
		const res = await $api.leader.featureFlags();
		if (res.data.data) {
			await store.dispatch('setFeatureFlags', res.data.data);
		}
	} catch (e) {
		console.log(e);
	}
};

const setReminderDay = async () => {
	try {
		const today = new Date();
		today.setDate(today.getDate() + 2);
		await $api.org.updateOrg({
			org: {
				reminder_lucahub_registration_date: today,
			},
		});
	} catch (e) {
		console.log(e);
	}
};
</script>
